/**
 * Thin fetch wrapper for the Good-time Casino server.
 *
 * Base URL resolves to {@code VITE_API_BASE_URL} (production) or {@code /v1}
 * (dev — Vite proxy at vite.config.ts forwards /v1 to localhost:8090).
 */

import { getSecure, setSecure } from '../storage/secureStore';

/** The API base prefix (includes `/v1`). `VITE_API_BASE_URL` in prod (e.g.
 *  `https://api.goodtime.casino/v1`), or `/v1` in dev (Vite proxy). Exported so
 *  the sync hooks hit the same origin instead of a relative `/v1` that breaks
 *  when the app is served from a different host than the API. */
export const BASE: string = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/v1';

/** The API ORIGIN with no path prefix (BASE minus the trailing `/v1`). Used by
 *  the room-engine endpoints that live under `/api` (W6 RoomStreamController)
 *  rather than `/v1`. Empty in dev (relative → Vite proxy); the full origin
 *  (e.g. `https://api.goodtime.casino`) in prod, so `/api` resolves to the API
 *  host even when the app is served from a different origin (play.goodtime.casino). */
export const ORIGIN: string = BASE.replace(/\/v1\/?$/, '');

const TOKEN_KEY = 'goodtime.playerToken';
let tokenCache: string | null | undefined;

/** This device's player token (sent as {@code X-Player-Token}), or null for a
 *  player from before tokens whose app hasn't claimed one yet. */
export async function playerToken(): Promise<string | null> {
  if (tokenCache === undefined) tokenCache = await getSecure(TOKEN_KEY);
  return tokenCache;
}

export async function setPlayerToken(token: string): Promise<void> {
  tokenCache = token;
  await setSecure(TOKEN_KEY, token);
}

/** The header that proves this device is the player, for calls that don't
 *  go through {@link api}. Empty without a token. */
export async function authHeaders(): Promise<Record<string, string>> {
  const token = await playerToken();
  return token ? { 'X-Player-Token': token } : {};
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`API ${status}: ${body || '(no body)'}`);
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(await authHeaders()) };
  const initHeaders = init?.headers as Record<string, string> | undefined;
  if (initHeaders) {
    Object.assign(headers, initHeaders);
  }
  const response = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new ApiError(response.status, body);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/** Build an SSE-friendly URL on the same base. EventSource doesn't accept custom headers. */
export function eventSourceUrl(path: string): string {
  return `${BASE}${path}`;
}
